import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../api/axios';
import { AdminPageHeader } from './AdminUi';
import './admin.css';

const EMPTY = {
  username: '',
  email: '',
  first_name: '',
  last_name: '',
  current_password: '',
  new_password: '',
  confirm_password: '',
};

function resolveImageUrl(image) {
  if (!image) return null;
  if (image.startsWith('http://') || image.startsWith('https://') || image.startsWith('blob:')) {
    return image;
  }
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  if (image.startsWith('/media/')) return `${apiUrl}${image}`;
  return `${apiUrl}/media/${image}`;
}

export default function AdminProfile() {
  const fileRef = useRef(null);
  const [form, setForm] = useState(EMPTY);
  const [meta, setMeta] = useState({ is_superuser: false, last_login: null, date_joined: null });
  const [imageUrl, setImageUrl] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [clearImage, setClearImage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorBusy, setTwoFactorBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [profileRes, twoFactorRes] = await Promise.all([
          api.get('admin/profile/'),
          api.get('admin/two-factor/').catch(() => ({ data: { two_factor_enabled: false } })),
        ]);
        if (!alive) return;
        const r = profileRes;
        setForm({
          username: r.data.username || '',
          email: r.data.email || '',
          first_name: r.data.first_name || '',
          last_name: r.data.last_name || '',
          current_password: '',
          new_password: '',
          confirm_password: '',
        });
        setMeta({
          is_superuser: !!r.data.is_superuser,
          last_login: r.data.last_login,
          date_joined: r.data.date_joined,
        });
        setTwoFactorEnabled(!!twoFactorRes.data?.two_factor_enabled);
        const img = resolveImageUrl(r.data.image);
        setImageUrl(img);
        if (img) localStorage.setItem('adminImage', img);
        else localStorage.removeItem('adminImage');
      } catch (e) {
        toast.error(e.response?.data?.error || 'Failed to load profile');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    return () => {
      if (preview && preview.startsWith('blob:')) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const toggleTwoFactor = async () => {
    const next = !twoFactorEnabled;
    if (next && !form.email.trim()) {
      toast.error('Add an email on your profile before enabling 2FA');
      return;
    }
    setTwoFactorBusy(true);
    try {
      const r = await api.post('admin/two-factor/', { two_factor_enabled: next });
      setTwoFactorEnabled(!!r.data.two_factor_enabled);
      toast.success(
        r.data.msg || `Two-factor authentication ${next ? 'enabled' : 'disabled'}`
      );
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update 2FA settings');
    } finally {
      setTwoFactorBusy(false);
    }
  };

  const onPickImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (preview && preview.startsWith('blob:')) URL.revokeObjectURL(preview);
    setImageFile(file);
    setPreview(URL.createObjectURL(file));
    setClearImage(false);
  };

  const removeImage = () => {
    if (preview && preview.startsWith('blob:')) URL.revokeObjectURL(preview);
    setImageFile(null);
    setPreview(null);
    setClearImage(true);
    if (fileRef.current) fileRef.current.value = '';
  };

  const save = async (e) => {
    e.preventDefault();
    if (form.new_password || form.confirm_password) {
      if (form.new_password !== form.confirm_password) {
        toast.error('New password and confirm password do not match');
        return;
      }
      if (!form.current_password) {
        toast.error('Enter current password to change password');
        return;
      }
    }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('username', form.username.trim());
      fd.append('email', form.email.trim());
      fd.append('first_name', form.first_name.trim());
      fd.append('last_name', form.last_name.trim());
      if (form.new_password) {
        fd.append('current_password', form.current_password);
        fd.append('new_password', form.new_password);
      }
      if (imageFile) fd.append('image', imageFile);
      if (clearImage) fd.append('clear_image', 'true');

      const r = await api.patch('admin/profile/', fd);
      localStorage.setItem('adminUsername', r.data.username || form.username);
      const img = resolveImageUrl(r.data.image);
      if (img) localStorage.setItem('adminImage', img);
      else localStorage.removeItem('adminImage');
      window.dispatchEvent(new Event('admin-profile-updated'));
      toast.success(r.data.msg || 'Profile updated');
      setForm((prev) => ({
        ...prev,
        username: r.data.username || prev.username,
        email: r.data.email || '',
        first_name: r.data.first_name || '',
        last_name: r.data.last_name || '',
        current_password: '',
        new_password: '',
        confirm_password: '',
      }));
      setImageUrl(img);
      if (preview && preview.startsWith('blob:')) URL.revokeObjectURL(preview);
      setPreview(null);
      setImageFile(null);
      setClearImage(false);
      if (fileRef.current) fileRef.current.value = '';
    } catch (err) {
      toast.error(err.response?.data?.error || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const initial = (form.username || 'A').slice(0, 1).toUpperCase();
  const displaySrc = clearImage ? null : (preview || imageUrl);

  return (
    <div>
      <AdminPageHeader
        title="My Profile"
        subtitle="Update your admin account details"
      />

      {loading ? (
        <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
      ) : (
        <div className="row g-3">
          <div className="col-lg-4">
            <div className="admin-card p-4 text-center h-100">
              <div
                className="admin-profile-photo"
              >
                {displaySrc ? (
                  <img src={displaySrc} alt="" />
                ) : (
                  initial
                )}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="d-none"
                onChange={onPickImage}
              />
              <div className="d-flex justify-content-center gap-2 mb-3 flex-wrap">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary rounded-pill px-3"
                  onClick={() => fileRef.current?.click()}
                >
                  {displaySrc ? 'Change photo' : 'Upload photo'}
                </button>
                {displaySrc && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger rounded-pill px-3"
                    onClick={removeImage}
                  >
                    Remove
                  </button>
                )}
              </div>
              <h5 className="fw-bold mb-1">{form.username}</h5>
              <p className="text-muted small mb-2">{form.email || 'No email set'}</p>
              <span className={`admin-badge ${meta.is_superuser ? 'approved' : 'default'}`}>
                {meta.is_superuser ? 'Super Admin' : 'Staff Admin'}
              </span>
              <div className="mt-3 small text-muted text-start">
                <div className="mb-1">
                  <strong>Joined:</strong>{' '}
                  {meta.date_joined ? new Date(meta.date_joined).toLocaleString() : '—'}
                </div>
                <div>
                  <strong>Last login:</strong>{' '}
                  {meta.last_login ? new Date(meta.last_login).toLocaleString() : '—'}
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-8">
            <div className="admin-card p-4">
              <form onSubmit={save}>
                <h6 className="fw-bold mb-3">Account details</h6>
                <div className="row g-3 mb-4">
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">Username</label>
                    <input className="form-control" value={form.username} onChange={set('username')} required />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">Email</label>
                    <input type="email" className="form-control" value={form.email} onChange={set('email')} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">First name</label>
                    <input className="form-control" value={form.first_name} onChange={set('first_name')} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">Last name</label>
                    <input className="form-control" value={form.last_name} onChange={set('last_name')} />
                  </div>
                </div>

                <h6 className="fw-bold mb-3">Change password</h6>
                <p className="small text-muted mb-3">Leave blank if you do not want to change password.</p>
                <div className="row g-3 mb-4">
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold">Current password</label>
                    <input
                      type="password"
                      className="form-control"
                      value={form.current_password}
                      onChange={set('current_password')}
                      autoComplete="current-password"
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold">New password</label>
                    <input
                      type="password"
                      className="form-control"
                      value={form.new_password}
                      onChange={set('new_password')}
                      autoComplete="new-password"
                      minLength={8}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold">Confirm new password</label>
                    <input
                      type="password"
                      className="form-control"
                      value={form.confirm_password}
                      onChange={set('confirm_password')}
                      autoComplete="new-password"
                    />
                  </div>
                </div>

                <h6 className="fw-bold mb-3">Security</h6>
                <div className="d-flex align-items-center justify-content-between py-3 border rounded-3 px-3 mb-4">
                  <div className="d-flex align-items-center">
                    <div className="bg-light rounded-3 p-2 me-3">
                      <i className="bi bi-shield-lock text-muted fs-5" />
                    </div>
                    <div>
                      <p className="mb-0 fw-medium">Two-Factor Authentication</p>
                      <small className="text-muted">
                        After password login, verify with an OTP sent to your email.
                        {!form.email.trim() && ' Add an email above to enable.'}
                      </small>
                    </div>
                  </div>
                  <div className="form-check form-switch mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="adminTwoFactorEnabled"
                      checked={twoFactorEnabled}
                      onChange={toggleTwoFactor}
                      disabled={twoFactorBusy || (!form.email.trim() && !twoFactorEnabled)}
                      style={{ width: '3rem', height: '1.5rem', cursor: 'pointer' }}
                    />
                  </div>
                </div>

                <button type="submit" className="btn btn-primary rounded-pill px-4" disabled={saving}>
                  {saving ? 'Saving…' : 'Save profile'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
